import React, { useState } from "react";
import { useParams } from "react-router-dom";
import RecordPage from "../../components/RecordPage";
import MultiFileField from "../../components/MultiFileField";
import PledgeFiles from "./PledgeFiles";
import api from "../../core/api";
import { useApp } from "../../core/AppContext";
import { formatDate, todayIso } from "../../core/format";
import { pledgePrint } from "../../core/printConfigs";

const LABELS = {
  repair: { title: "تعهدنامه تعمیرات جزئی", usage: "نوع تعمیرات", loc: "محل انجام تعمیرات", dur: "مدت زمان تعمیرات" },
  crane: { title: "تعهدنامه جرثقیل", usage: "نوع استفاده از جرثقیل", loc: "محل استفاده از جرثقیل", dur: "مدت زمان مورد نیاز" },
  electricity: { title: "تعهدنامه استفاده از برق", usage: "نوع استفاده از برق", loc: "محل استفاده از برق", dur: "مدت زمان استفاده از برق" },
};

async function uploadMany(id, files, category) {
  if (!files?.length) return;
  const fd = new FormData();
  fd.append("category", category);
  files.forEach((f) => fd.append("files", f));
  await api.post(`/pledges/${id}/files`, fd, { timeout: 180000 });
}

export default function Pledges() {
  const { kind } = useParams();
  const { user } = useApp();
  const L = LABELS[kind] || LABELS.repair;
  const admin = user.role === "admin";
  const staff = user.role === "staff";

  const [filesFor, setFilesFor] = useState(null);
  const [tick, setTick] = useState(0);

  return (
    <>
<RecordPage key={`${kind}-${tick}`} hideFileButton title={L.title} endpoint="/pledges" query={{ kind }} multipart createLabel="ثبت تعهدنامه"
        canCreate={staff || admin} canEdit={admin} canDelete={admin} initial={{ pledgeDate: todayIso() }} fixed={{ kind }}
        subtitle={admin ? "همه‌ی تعهدنامه‌های ثبت‌شده" : "تعهدنامه‌هایی که شما ثبت کرده‌اید"}
        // اولین فایل تعهدنامه به‌عنوان فایل اصلی ارسال می‌شود؛ بقیه و معرفی‌نامه بعد از ثبت آپلود می‌شوند
        transformBody={(fd, form) => { if (form.pledgeFiles?.length) fd.append("file", form.pledgeFiles[0]); }}
        afterSave={async (data, form, { editing, row }) => {
          const id = editing ? row.id : data?.item?.id ?? data?.id;
          if (!id) throw new Error("no id");
          await uploadMany(id, (form.pledgeFiles || []).slice(1), "pledge");
          await uploadMany(id, form.introFiles, "intro");
        }}
        printReport={pledgePrint}
        rowExtra={(r) => (
          <button className="icon-btn" title="فایل‌ها و معرفی‌نامه" onClick={() => setFilesFor(r)}><i className="fa-solid fa-folder-open" /></button>
        )}
        columns={[
          { key: "pledgeDate", label: "تاریخ", render: (r) => formatDate(r.pledgeDate) },
          { key: "person", label: "فرد متعهد", render: (r) => <b>{r.firstName} {r.lastName}</b> },
          { key: "nationalId", label: "شماره ملی", render: (r) => <span dir="ltr">{r.nationalId}</span> },
          { key: "mobile", label: "موبایل", render: (r) => <span dir="ltr">{r.mobile}</span> },
          { key: "representative", label: "نماینده / شناور" },
          { key: "usageType", label: L.usage },
          { key: "location", label: "محل" },
          { key: "duration", label: "مدت" },
          ...(admin ? [{ key: "creatorName", label: "ثبت‌کننده" }] : []),
        ]}
        getFields={(form) => [
          { name: "pledgeDate", label: "تاریخ", type: "date", required: true, full: true },
          { name: "firstName", label: "نام فرد متعهد", required: true },
          { name: "lastName", label: "نام خانوادگی فرد متعهد", required: true },
          { name: "nationalId", label: "شماره ملی فرد متعهد", type: "nid", required: true },
          { name: "mobile", label: "شماره موبایل فرد متعهد", type: "mobile", required: true },
          { name: "representative", label: "نماینده کشتیرانی / شناور / سایر", required: true, full: true },
          { name: "usageType", label: L.usage, required: true },
          { name: "location", label: L.loc, required: true },
          { name: "duration", label: L.dur, required: true, placeholder: "مثلاً ۳ ساعت" },
      
          {
            name: "introFiles", label: "معرفی‌نامه", type: "custom", noBody: true, full: true,
            render: ({ form: f, setForm, errors }) => (
              <MultiFileField label="معرفی‌نامه (اختیاری)" files={f.introFiles || []} error={errors.introFiles}
                onChange={(v) => setForm((s) => ({ ...s, introFiles: v }))} />
            ),
          },
              {
            name: "pledgeFiles", label: "فایل‌های تعهدنامه", type: "custom", noBody: true, full: true, required: !form.id,
            render: ({ form: f, setForm, errors }) => (
              <MultiFileField label="فایل‌های تعهدنامه" required={!f.id} files={f.pledgeFiles || []} error={errors.pledgeFiles}
                hint={f.id ? "در ویرایش، اولین فایل جایگزین فایل اصلی می‌شود و بقیه به فایل‌ها اضافه می‌شوند." : undefined}
                onChange={(v) => setForm((s) => ({ ...s, pledgeFiles: v }))} />
            ),
          },
        ]} />
      {filesFor && <PledgeFiles pledge={filesFor} admin={admin} onClose={() => setFilesFor(null)} onChanged={() => setTick((t) => t + 1)} />}
    </>
  );
}